import { useEffect, useRef, useState } from "react";

const MANIFEST_URL = `${process.env.PUBLIC_URL || ""}/asset-manifest.json`;
const POLL_INTERVAL_MS = 60 * 1000;

// Reads the hashed main bundle path from the build's asset-manifest.json.
// CRA rewrites this hash on every meaningful build, so a change means a new deploy.
async function fetchCurrentVersion() {
  const res = await fetch(MANIFEST_URL, { cache: "no-store" });
  if (!res.ok) throw new Error(`manifest fetch failed: ${res.status}`);
  const manifest = await res.json();
  return manifest?.files?.["main.js"] || null;
}

/**
 * Detects when a newer build has been deployed while the tab is open.
 * Returns `true` once a new version is available (latched — never flips back).
 *
 * Polls on an interval and whenever the tab regains visibility/focus.
 * Transient fetch failures are ignored so a blip never triggers a false reload.
 */
export default function useVersionCheck() {
  const [newVersionAvailable, setNewVersionAvailable] = useState(false);
  const baselineRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      try {
        const version = await fetchCurrentVersion();
        if (cancelled || !version) return;
        if (baselineRef.current === null) {
          baselineRef.current = version;
        } else if (version !== baselineRef.current) {
          setNewVersionAvailable(true);
        }
      } catch {
        // Ignore transient errors (offline, deploy mid-swap, etc.)
      }
    };

    check();
    const interval = setInterval(check, POLL_INTERVAL_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") check();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);

    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, []);

  return newVersionAvailable;
}
